import { useState, useEffect, useRef } from 'react';
import MainLayout from '@/components/MainLayout';
import { 
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Search, ChevronRight, MessageSquare, AlertCircle, ArrowLeft, Clock } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { getUserMessages, markMessageAsRead, replyToMessage, subscribeToMessages } from '@/lib/supabase';
import { formatTimeAgo } from '@/lib/utils';
import { toast } from '@/hooks/use-toast';
import { Textarea } from '@/components/ui/textarea';
import { Send } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

interface Message {
  id: string;
  sender_id: string;
  receiver_id: string;
  subject: string;
  content: string;
  related_post_id?: string;
  is_read?: boolean;
  created_at?: string;
  updated_at?: string;
  parent_id?: string;
  sender?: {
    id: string;
    username: string;
    display_name: string | null;
    avatar_url: string | null;
  };
}

const Messages = () => {
  const { user } = useAuth();
  const [messages, setMessages] = useState<Message[]>([]);
  const [selectedMessage, setSelectedMessage] = useState<Message | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [replyMode, setReplyMode] = useState(false);
  const [replyContent, setReplyContent] = useState('');
  const [isSendingReply, setIsSendingReply] = useState(false);
  const [threadMessages, setThreadMessages] = useState<Message[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const cleanupRef = useRef<() => void | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (user) {
      loadMessages();
      
      // Set up real-time subscription for new messages
      if (cleanupRef.current) {
        cleanupRef.current();
      }
      
      const cleanup = subscribeToMessages(user.id, (newMessage) => {
        // Add new message to state
        setMessages(prevMessages => {
          // Check if the message already exists to avoid duplicates
          const messageExists = prevMessages.some(m => m.id === newMessage.id);
          if (messageExists) return prevMessages;
          
          // Add the new message at the beginning
          return [newMessage, ...prevMessages];
        });
        
        // Update thread messages if this is part of the current thread
        if (selectedMessage && 
            (newMessage.parent_id === selectedMessage.id || 
             newMessage.id === selectedMessage.parent_id)) {
          setThreadMessages(prev => [...prev, newMessage]);
        }
        
        // Show notification for new message
        toast({
          title: "New Message",
          description: `${newMessage.sender?.display_name || newMessage.sender?.username || 'Someone'} sent you a message.`,
        });
        
        // Update unread count
        setUnreadCount(prev => prev + 1);
      });
      
      cleanupRef.current = cleanup;
      
      // Listen for sign out event
      const handlePreSignout = () => {
        console.log('Messages component: Cleaning up subscriptions before sign out');
        if (cleanupRef.current) {
          cleanupRef.current();
          cleanupRef.current = null;
        }
      };
      
      window.addEventListener('app:pre-signout', handlePreSignout);
      
      // Cleanup subscription on unmount
      return () => {
        if (cleanup) cleanup();
        window.removeEventListener('app:pre-signout', handlePreSignout);
      };
    }
  }, [user, selectedMessage]);

  // Scroll to bottom of thread when new messages come in
  useEffect(() => {
    if (messagesEndRef.current && threadMessages.length > 0) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [threadMessages]);

  const loadMessages = async () => {
    if (!user) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const fetchedMessages = await getUserMessages(user.id);
      setMessages(fetchedMessages);
      
      // Count unread messages
      const unread = fetchedMessages.filter(msg => 
        !msg.is_read && msg.receiver_id === user.id
      ).length;
      setUnreadCount(unread);
    } catch (error) {
      console.error('Error loading messages:', error);
      setError('Unable to load messages. Please check your connection and try again.');
      toast({
        title: "Connection Error",
        description: "Failed to load messages. Please try again later.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const loadThreadMessages = (message: Message) => {
    // Start with the selected message
    const thread: Message[] = [message];
    
    // Find related messages (same parent or children of this message)
    messages.forEach(msg => {
      // Include messages that share the same parent
      if (message.parent_id && msg.parent_id === message.parent_id && msg.id !== message.id) {
        thread.push(msg);
      }
      
      // Include replies to this message
      if (msg.parent_id === message.id) {
        thread.push(msg);
      }
      
      // Include parent message if this is a reply
      if (message.parent_id === msg.id) {
        thread.push(msg);
      }
    });
    
    // Sort by created_at
    thread.sort((a, b) => {
      if (!a.created_at || !b.created_at) return 0;
      return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
    });
    
    setThreadMessages(thread);
  };

  const handleMessageClick = async (message: Message) => {
    try {
      setSelectedMessage(message);
      
      // Load thread messages
      loadThreadMessages(message);
      
      if (!message.is_read && message.receiver_id === user?.id) {
        await markMessageAsRead(message.id);
        
        // Update message in the list
        setMessages(prevMessages => 
          prevMessages.map(m => 
            m.id === message.id ? { ...m, is_read: true } : m
          )
        );
        
        // Update unread count
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
      
      // Reset reply mode when viewing a new message
      setReplyMode(false);
      setReplyContent('');
    } catch (error) {
      console.error('Error marking message as read:', error);
      toast({
        title: "Error",
        description: "Failed to mark message as read.",
        variant: "destructive",
      });
    }
  };

  const backToInbox = () => {
    setSelectedMessage(null);
    setReplyMode(false);
    setReplyContent('');
    setThreadMessages([]);
  };

  const handleRetry = () => {
    loadMessages();
  };

  const toggleReplyMode = () => {
    setReplyMode(!replyMode);
    if (!replyMode) {
      // Set default reply prefix
      setReplyContent('');
    }
  };

  const handleSendReply = async () => {
    if (!user || !selectedMessage || !replyContent.trim()) return;
    
    setIsSendingReply(true);
    try {
      const result = await replyToMessage({
        sender_id: user.id,
        receiver_id: selectedMessage.sender_id,
        subject: `Re: ${selectedMessage.subject}`,
        content: replyContent,
        parent_id: selectedMessage.id,
        related_post_id: selectedMessage.related_post_id
      });

      if (result) {
        toast({
          title: "Reply sent",
          description: "Your reply has been sent successfully.",
        });
        
        // Add the new message to the messages list
        setMessages(prev => [result, ...prev]);
        
        // Add to thread messages
        setThreadMessages(prev => [...prev, result]);
        
        // Exit reply mode
        setReplyMode(false);
        setReplyContent('');
      }
    } catch (error) {
      console.error('Error sending reply:', error);
      toast({
        title: "Error",
        description: "Failed to send reply. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSendingReply(false);
    }
  };

  const getMessagePreview = (message: Message) => {
    // Check if this message has replies
    const hasReplies = messages.some(m => m.parent_id === message.id);
    
    // Check if this is a reply to another message
    const isReply = !!message.parent_id;
    
    return (
      <div className="flex flex-col">
        <div className="flex gap-2 items-center">
          {message.subject}
          {hasReplies && (
            <Badge variant="outline" className="text-xs">Thread</Badge>
          )}
          {isReply && (
            <Badge variant="outline" className="text-xs">Reply</Badge>
          )}
        </div>
        <div className="text-sm text-vibebops-textMuted truncate">
          {message.content}
        </div>
      </div>
    );
  };

  const filteredMessages = messages.filter(message => 
    message.subject.toLowerCase().includes(searchQuery.toLowerCase()) ||
    message.content.toLowerCase().includes(searchQuery.toLowerCase()) ||
    message.sender?.display_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    message.sender?.username?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Group messages by thread
  const groupedMessages: Message[] = [];
  const seenThreads = new Set<string>();
  
  filteredMessages.forEach(message => {
    // If this is a new thread or a standalone message
    if ((!message.parent_id && !seenThreads.has(message.id)) || 
        (message.parent_id && !seenThreads.has(message.parent_id))) {
      
      // Mark thread as seen
      if (message.parent_id) {
        seenThreads.add(message.parent_id);
      } else {
        seenThreads.add(message.id);
      }
      
      // Add to grouped messages
      groupedMessages.push(message);
    }
  });

  return (
    <MainLayout>
      <Card className="max-w-5xl mx-auto">
        <CardHeader>
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5" />
                Messages
              </CardTitle>
              {unreadCount > 0 && (
                <Badge className="bg-purple-500 hover:bg-purple-600">
                  {unreadCount} unread
                </Badge>
              )}
            </div>
            <div className="relative w-64">
              <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-vibebops-textMuted" />
              <Input
                type="text"
                placeholder="Search messages..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {selectedMessage ? (
            <div className="p-6">
              <Button 
                variant="outline" 
                className="mb-4" 
                onClick={backToInbox}
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Inbox
              </Button>
              
              {replyMode ? (
                <div className="space-y-4">
                  <div className="flex justify-between items-center border-b pb-4 border-vibebops-border">
                    <h3 className="text-xl font-bold">Reply to: {selectedMessage.subject}</h3>
                  </div>
                  
                  <div className="mb-4 p-4 bg-vibebops-card border border-vibebops-border rounded-lg">
                    <p className="text-sm text-vibebops-textMuted mb-2">Original message from {selectedMessage.sender?.display_name || selectedMessage.sender?.username}:</p>
                    <p className="text-sm italic">{selectedMessage.content}</p>
                  </div>
                  
                  <Textarea 
                    placeholder="Write your reply here..." 
                    value={replyContent}
                    onChange={(e) => setReplyContent(e.target.value)}
                    className="min-h-[120px] mb-2"
                  />
                  
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={toggleReplyMode}>
                      Cancel
                    </Button>
                    <Button 
                      onClick={handleSendReply} 
                      disabled={!replyContent.trim() || isSendingReply}
                    >
                      {isSendingReply ? (
                        <>
                          <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"></div>
                          <span>Sending...</span>
                        </>
                      ) : (
                        <>
                          <Send className="h-4 w-4 mr-2" />
                          <span>Send Reply</span>
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex justify-between items-center border-b pb-4 border-vibebops-border">
                    <h3 className="text-xl font-bold">{selectedMessage.subject}</h3>
                  </div>
                  
                  {/* Thread view */}
                  <div className="space-y-4">
                    {threadMessages.map((message, index) => (
                      <div key={message.id} className={`p-4 rounded-lg ${
                        message.sender_id === user?.id 
                          ? 'bg-vibebops-highlight/20 ml-8' 
                          : 'bg-vibebops-card border border-vibebops-border mr-8'
                      }`}>
                        <div className="flex items-center mb-2">
                          <div className="h-8 w-8 rounded-full bg-gradient-to-r from-pink-500 to-violet-500 border-2 border-black mr-3 overflow-hidden">
                            {message.sender?.avatar_url && (
                              <img 
                                src={message.sender.avatar_url} 
                                alt={message.sender.display_name || message.sender.username} 
                                className="h-full w-full object-cover"
                              />
                            )}
                          </div>
                          <div className="flex-1">
                            <div className="flex justify-between items-center">
                              <p className="font-bold">
                                {message.sender_id === user?.id 
                                  ? 'You' 
                                  : (message.sender?.display_name || message.sender?.username)}
                              </p>
                              <span className="text-xs text-vibebops-textMuted flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                {message.created_at && formatTimeAgo(message.created_at)}
                              </span>
                            </div>
                          </div>
                        </div>
                        <p className="whitespace-pre-wrap">{message.content}</p>
                      </div>
                    ))}
                    <div ref={messagesEndRef} /> {/* Scroll anchor */}
                  </div>
                  
                  <div className="pt-4 border-t border-vibebops-border">
                    <Button onClick={toggleReplyMode}>
                      <Send className="h-4 w-4 mr-2" />
                      Reply
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              {isLoading ? (
                <div className="flex justify-center items-center py-8">
                  <div className="h-8 w-8 animate-spin rounded-full border-4 border-current border-t-transparent"></div>
                </div>
              ) : error ? (
                <div className="flex flex-col items-center justify-center py-8 px-4 text-center">
                  <AlertCircle className="h-8 w-8 text-red-500 mb-2" />
                  <p className="text-vibebops-textMuted mb-4">{error}</p>
                  <Button onClick={handleRetry} variant="outline">
                    Try Again
                  </Button>
                </div>
              ) : filteredMessages.length === 0 ? (
                <div className="text-center py-8 text-vibebops-textMuted">
                  {searchQuery ? "No messages found matching your search" : "No messages yet"}
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-1/4">From</TableHead>
                      <TableHead className="w-2/4">Message</TableHead>
                      <TableHead className="w-24 text-right">Date</TableHead>
                      <TableHead className="w-10"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {groupedMessages.map((message) => (
                      <TableRow 
                        key={message.id}
                        className={`cursor-pointer hover:bg-vibebops-highlight/50 ${
                          !message.is_read && message.receiver_id === user?.id 
                            ? 'font-medium bg-vibebops-highlight/30' 
                            : ''
                        }`}
                        onClick={() => handleMessageClick(message)}
                      >
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div className="h-8 w-8 rounded-full bg-gradient-to-r from-pink-500 to-violet-500 border border-black overflow-hidden flex-shrink-0">
                              {message.sender?.avatar_url && (
                                <img 
                                  src={message.sender.avatar_url} 
                                  alt={message.sender.display_name || message.sender.username} 
                                  className="h-full w-full object-cover"
                                />
                              )}
                            </div>
                            <span>{message.sender?.display_name || message.sender?.username}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          {getMessagePreview(message)}
                        </TableCell>
                        <TableCell className="text-right text-vibebops-textMuted">
                          {message.created_at && formatTimeAgo(message.created_at)}
                        </TableCell>
                        <TableCell>
                          <ChevronRight className="h-4 w-4 text-vibebops-textMuted" />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </MainLayout>
  );
};

export default Messages;
